import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, Check, X, Filter, AlertCircle, CheckSquare, Clock, ArrowUpRight, MessageCircle } from 'lucide-react';
import api from '../utils/api';
import { fmtDate } from '../utils/helpers';
import toast from 'react-hot-toast';
import { buildWaUrl } from '../utils/whatsapp';

const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];
const STATUSES_UI = ['Pending', 'In Progress', 'Done'];

const PRIORITY_COLOR = {
  'Low':    'text-gray-500 bg-gray-50 border-gray-200',
  'Medium': 'text-blue-600 bg-blue-50 border-blue-200',
  'High':   'text-orange-600 bg-orange-50 border-orange-200',
  'Urgent': 'text-red-600 bg-red-50 border-red-200',
};

export default function ActionItemsPage() {
  const [items, setItems] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'Pending', assigned_to: '', priority: '', from: '', to: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get('/meetings/action-items', { params });
      setItems(r.data);
    } catch { toast.error('Failed to load action items'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/auth/users').then(r => setStaff(r.data)).catch(() => {}); }, []);

  const toggleDone = async item => {
    const newStatus = item.status === 'Done' ? 'Pending' : 'Done';
    try {
      await api.put(`/meetings/${item.meeting_id}/action-items/${item.id}`, { ...item, status: newStatus });
      load();
    } catch { toast.error('Failed to update'); }
  };

  // Group by meeting
  const grouped = {};
  items.forEach(item => {
    const key = item.meeting_id;
    if (!grouped[key]) grouped[key] = { meetingId: item.meeting_id, meetingTitle: item.meeting_title, meetingDate: item.meeting_date, clientName: item.client_name, items: [] };
    grouped[key].items.push(item);
  });

  const stats = {
    total: items.length,
    pending: items.filter(i => i.status === 'Pending').length,
    inProgress: items.filter(i => i.status === 'In Progress').length,
    done: items.filter(i => i.status === 'Done').length,
    overdue: items.filter(i => i.status !== 'Done' && i.due_date && i.due_date < new Date().toISOString().split('T')[0]).length,
  };

  const clearFilters = () => setFilters({ status: '', assigned_to: '', priority: '', from: '', to: '' });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f] flex items-center gap-2"><ClipboardList size={24} /> Action Items</h1>
          <p className="text-gray-500 text-sm">Action items across all meetings</p>
        </div>
        <Link to="/meetings" className="btn-secondary flex items-center gap-2 text-sm">← Back to Meetings</Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Total', value: stats.total, color: 'border-gray-300' },
          { label: 'Pending', value: stats.pending, color: 'border-orange-500', badge: 'text-orange-600' },
          { label: 'In Progress', value: stats.inProgress, color: 'border-blue-500', badge: 'text-blue-600' },
          { label: 'Done', value: stats.done, color: 'border-green-500', badge: 'text-green-600' },
          { label: 'Overdue', value: stats.overdue, color: 'border-red-500', badge: 'text-red-600' },
        ].map(c => (
          <div key={c.label} className={`card border-l-4 ${c.color} py-3`}>
            <div className={`text-2xl font-bold ${c.badge || 'text-gray-800'}`}>{c.value}</div>
            <div className="text-xs text-gray-500">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap gap-3 items-end">
          <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} className="input-field w-36 text-sm">
            <option value="">All Statuses</option>{STATUSES_UI.map(s => <option key={s}>{s}</option>)}
          </select>
          <select value={filters.priority} onChange={e => setFilters(f => ({ ...f, priority: e.target.value }))} className="input-field w-32 text-sm">
            <option value="">All Priorities</option>{PRIORITIES.map(p => <option key={p}>{p}</option>)}
          </select>
          <select value={filters.assigned_to} onChange={e => setFilters(f => ({ ...f, assigned_to: e.target.value }))} className="input-field w-40 text-sm">
            <option value="">All Assignees</option>{staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <div className="flex gap-2 items-center">
            <span className="text-xs text-gray-400">Due:</span>
            <input type="date" value={filters.from} onChange={e => setFilters(f => ({ ...f, from: e.target.value }))} className="input-field w-36 text-sm" />
            <span className="text-xs text-gray-400">to</span>
            <input type="date" value={filters.to} onChange={e => setFilters(f => ({ ...f, to: e.target.value }))} className="input-field w-36 text-sm" />
          </div>
          {Object.values(filters).some(v => v) && (
            <button onClick={clearFilters} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 px-2 py-1 border border-red-200 rounded-lg"><X size={12} /> Clear</button>
          )}
          <span className="text-xs text-gray-400 ml-auto">{items.length} items</span>
        </div>
      </div>

      {/* List grouped by meeting */}
      {loading ? (
        <div className="card text-center py-12 text-gray-400">Loading...</div>
      ) : items.length === 0 ? (
        <div className="card text-center py-12">
          <ClipboardList size={36} className="mx-auto mb-3 text-gray-300" />
          <p className="text-gray-400">No action items found.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {Object.values(grouped).map(group => (
            <div key={group.meetingId} className="card">
              <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-100">
                <div>
                  <Link to={`/meetings/${group.meetingId}#mom`} className="font-semibold text-[#1e3a5f] hover:underline text-sm flex items-center gap-1">
                    {group.meetingTitle} <ArrowUpRight size={13} />
                  </Link>
                  <div className="text-xs text-gray-400 mt-0.5">{fmtDate(group.meetingDate)}{group.clientName ? ` · ${group.clientName}` : ''}</div>
                </div>
                <span className="text-xs text-gray-400">{group.items.length} item{group.items.length !== 1 ? 's' : ''}</span>
              </div>

              <div className="space-y-2">
                {group.items.map(item => {
                  const isOverdue = item.status !== 'Done' && item.due_date && item.due_date < new Date().toISOString().split('T')[0];
                  return (
                    <div key={item.id} className={`flex items-start gap-3 p-3 rounded-xl border ${item.status === 'Done' ? 'bg-gray-50 border-gray-100 opacity-70' : isOverdue ? 'bg-red-50 border-red-200' : 'bg-white border-gray-100'}`}>
                      <button onClick={() => toggleDone(item)} className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center transition-colors ${item.status === 'Done' ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 hover:border-green-400'}`}>
                        {item.status === 'Done' && <Check size={12} />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${item.status === 'Done' ? 'line-through text-gray-400' : 'text-gray-800'}`}>{item.action_item}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-1.5">
                          <span className={`text-xs px-1.5 py-0.5 rounded border font-medium ${PRIORITY_COLOR[item.priority]}`}>{item.priority}</span>
                          {item.assignee_name && <span className="text-xs text-gray-500">→ {item.assignee_name}</span>}
                          {item.due_date && <span className={`text-xs flex items-center gap-1 ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-400'}`}><Clock size={11} />{isOverdue ? 'Overdue · ' : ''}{fmtDate(item.due_date)}</span>}
                          <span className={`text-xs px-1.5 py-0.5 rounded ${item.status === 'Done' ? 'bg-green-100 text-green-700' : item.status === 'In Progress' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}>{item.status}</span>
                        </div>
                        {item.notes && <p className="text-xs text-gray-400 mt-1 italic">{item.notes}</p>}
                      </div>
                      <Link to={`/meetings/${item.meeting_id}`} className="flex-shrink-0 p-1 text-gray-300 hover:text-blue-400 rounded" title="Go to meeting"><ArrowUpRight size={14} /></Link>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
